import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Filter, Pencil, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import TrainingRecordDialog from "@/components/training/TrainingRecordDialog";

const STATUSES = [
  { v: "compliant",     label: "Compliant" },
  { v: "expiring",      label: "Expiring Soon" },
  { v: "expired",       label: "Expired" },
  { v: "not_completed", label: "Not Completed" },
  { v: "in_progress",   label: "In Progress" },
];

const EXPIRY_PERIODS = [
  { v: "expired", label: "Already expired" },
  { v: "30d",     label: "Next 30 days" },
  { v: "60d",     label: "Next 60 days" },
  { v: "90d",     label: "Next 90 days" },
];

function fmt(v) { return v ? String(v).slice(0, 10) : "—"; }
function humanize(v) { return v ? String(v).replace(/_/g, " ") : "—"; }

function StatusBadge({ status }) {
  const map = {
    compliant:     { label: "Compliant",       bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
    expiring:      { label: "Expiring Soon",   bg: "bg-amber-100",   text: "text-amber-900",   border: "border-amber-300" },
    expired:       { label: "Expired",         bg: "bg-red-100",     text: "text-red-800",     border: "border-red-200" },
    not_completed: { label: "Not Completed",   bg: "bg-slate-100",   text: "text-slate-700",   border: "border-slate-200" },
    in_progress:   { label: "In Progress",     bg: "bg-sky-100",     text: "text-sky-800",     border: "border-sky-200" },
  };
  const s = map[status] || map.not_completed;
  return <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>{s.label}</span>;
}

export default function RecordsTab({ search, onOpenStaff, onChanged, canManage, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ site_id: "", category: "", status: "", expiry_period: "" });
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (search && search.trim()) params.search = search.trim();
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const r = await api.get("/training/records", { params });
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filters, search]);

  useEffect(() => { load(); }, [load, refreshTick]);

  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
    api.get("/training/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this training record? This cannot be undone.")) return;
    try {
      await api.delete(`/training/${id}`);
      toast.success("Record deleted");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeFilters = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  return (
    <div className="space-y-3" data-testid="training-records-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500 tabular-nums">{rows.length} record{rows.length === 1 ? "" : "s"}</div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="btn-training-toggle-filters" className={`h-8 text-xs ${showFilters ? "bg-slate-100" : ""}`}>
          <Filter className="w-3.5 h-3.5 mr-1.5" /> Filters {activeFilters > 0 && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-800">{activeFilters}</span>}
        </Button>
      </div>
      {showFilters && (
        <div className="p-3 rounded-lg border border-slate-200 bg-white grid grid-cols-2 md:grid-cols-4 gap-2">
          <FilterSelect label="Site" value={filters.site_id} onChange={(v) => setFilters({ ...filters, site_id: v })} options={sites.map((s) => ({ v: s.id, label: s.name }))} testid="filter-training-site" />
          <FilterSelect label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={categories.map((c) => ({ v: c, label: c }))} testid="filter-training-category" />
          <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={STATUSES.map((s) => ({ v: s.v, label: s.label }))} testid="filter-training-status" />
          <FilterSelect label="Expiry period" value={filters.expiry_period} onChange={(v) => setFilters({ ...filters, expiry_period: v })} options={EXPIRY_PERIODS.map((e) => ({ v: e.v, label: e.label }))} testid="filter-training-expiry" />
          <div className="col-span-2 md:col-span-4 flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setFilters({ site_id: "", category: "", status: "", expiry_period: "" })} data-testid="btn-training-clear-filters" className="h-7 text-xs">Clear</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-slate-500" data-testid="training-records-loading">Loading records…</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-sm text-slate-500">No training records match the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full" data-testid="training-records-table">
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th>Course</th>
                  <th>Category</th>
                  <th>Provider</th>
                  <th>Completed</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  <th>Certificate</th>
                  <th className="w-20 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`training-row-${r.id}`}>
                    <td>
                      {r.resolved_staff_user_id ? (
                        <button
                          onClick={() => onOpenStaff?.(r.resolved_staff_user_id)}
                          className="text-slate-900 hover:text-sky-700 hover:underline text-left"
                          data-testid={`training-staff-link-${r.id}`}
                        >{r.resolved_staff_name}</button>
                      ) : (
                        <span className="text-slate-700">{r.resolved_staff_name || "—"}</span>
                      )}
                    </td>
                    <td>{r.resolved_course_name}</td>
                    <td className="text-slate-500">{humanize(r.resolved_category)}</td>
                    <td className="text-slate-500">{r.provider || "—"}</td>
                    <td className="tabular-nums">{fmt(r.completed_date)}</td>
                    <td className="tabular-nums">{fmt(r.expiry_date)}</td>
                    <td><StatusBadge status={r.status_computed} /></td>
                    <td>
                      {r.has_certificate ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-700"><FileText className="w-3.5 h-3.5" /> {r.certificate_id || "on file"}</span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {canManage && <>
                        <button onClick={() => setEditRow(r)} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`btn-edit-training-${r.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => del(r.id)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`btn-delete-training-${r.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TrainingRecordDialog
        open={!!editRow}
        record={editRow}
        onOpenChange={(v) => { if (!v) setEditRow(null); }}
        onSaved={() => { setEditRow(null); onChanged?.(); }}
      />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-0.5 w-full h-8 text-sm border border-slate-300 rounded-md bg-white px-2 capitalize"
      >
        <option value="">All</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </label>
  );
}
